/**
 * Вкладка «Удобства» в Настройках (правки Артёма 2026-06-17).
 *
 * Управляемый каталог удобств тенанта: список с иконками, кнопка
 * «+ Добавить удобство» (название + пикер иконки), правка названия/иконки,
 * удаление. Своё удобство добавляется. Поверх useAmenities/useIcons.
 *
 * RBAC: canManage — создание/правка/удаление только управляющим ролям
 * (мутации дополнительно гейтятся на бэке, /v1/amenities → mgmt).
 */
import { useMemo, useState } from 'react';
import { Button, Card, Skeleton } from '@hsc/ui';
import type { AmenityView } from '@hsc/types';
import { DbIcon } from '../../shared/nav/DbIcon.js';
import { IconPicker } from './IconPicker.js';
import {
  iconMap,
  useAmenities,
  useCreateAmenity,
  useDeleteAmenity,
  useIcons,
  useUpdateAmenity,
} from './useAmenities.js';

export interface AmenitiesPanelProps {
  canManage?: boolean;
}

export function AmenitiesPanel({ canManage = false }: AmenitiesPanelProps): JSX.Element {
  const amenitiesQ = useAmenities();
  const iconsQ = useIcons();
  const icons = useMemo(() => iconMap(iconsQ.data), [iconsQ.data]);

  // null — форма закрыта; '' — создание нового; id — правка существующего.
  const [editing, setEditing] = useState<string | null>(null);

  // Создание/правка удобства — ОТДЕЛЬНЫЙ экран (форма вместо списка), не
  // инлайн над списком (feedback_every_form_is_a_separate_screen).
  if (editing !== null) {
    return (
      <section data-testid="amenities-panel">
        <AmenityForm
          amenity={editing === '' ? undefined : amenitiesQ.data?.find((a) => a.id === editing)}
          onDone={() => setEditing(null)}
        />
      </section>
    );
  }

  return (
    <section data-testid="amenities-panel">
      {canManage && (
        <div className="amenity-toolbar">
          <Button onClick={() => setEditing('')} data-testid="amenity-add">
            + Добавить удобство
          </Button>
        </div>
      )}

      {amenitiesQ.isLoading ? (
        <div className="fleet-grid" data-testid="amenities-skeleton">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} height={56} radius={12} />
          ))}
        </div>
      ) : amenitiesQ.isError ? (
        <Card>
          <p role="alert">Не удалось загрузить удобства. Попробуйте обновить страницу.</p>
        </Card>
      ) : (amenitiesQ.data ?? []).length === 0 ? (
        <Card>
          <p>Удобств пока нет. {canManage && 'Добавьте первое кнопкой выше.'}</p>
        </Card>
      ) : (
        <ul className="amenity-list" data-testid="amenities-list">
          {(amenitiesQ.data ?? []).map((a) => (
            <li
              key={a.id}
              className={`amenity-row${a.is_active ? '' : ' is-inactive'}`}
              data-testid={`amenity-${a.code}`}
            >
              <span className="amenity-row__icon">
                <DbIcon icon={a.icon_id ? icons.get(a.icon_id) : undefined} size={20} />
              </span>
              <span className="amenity-row__name">{a.name}</span>
              {canManage && (
                <span className="amenity-row__actions">
                  <Button
                    variant="ghost"
                    onClick={() => setEditing(a.id)}
                    data-testid={`amenity-${a.code}-edit`}
                  >
                    Изменить
                  </Button>
                  <DeleteAmenityButton id={a.id} code={a.code} />
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function DeleteAmenityButton({ id, code }: { id: string; code: string }): JSX.Element {
  const del = useDeleteAmenity();
  return (
    <Button
      variant="ghost"
      disabled={del.isPending}
      onClick={() => del.mutate(id)}
      data-testid={`amenity-${code}-delete`}
    >
      {del.isPending ? '…' : 'Удалить'}
    </Button>
  );
}

function AmenityForm({
  amenity,
  onDone,
}: {
  amenity?: AmenityView | undefined;
  onDone: () => void;
}): JSX.Element {
  const isEdit = amenity !== undefined;
  const create = useCreateAmenity();
  const update = useUpdateAmenity();

  const [name, setName] = useState(amenity?.name ?? '');
  const [iconId, setIconId] = useState<string | null>(amenity?.icon_id ?? null);

  const pending = create.isPending || update.isPending;
  const isError = create.isError || update.isError;
  const canSubmit = name.trim().length > 0 && !pending;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    if (isEdit && amenity) {
      update.mutate(
        { id: amenity.id, input: { name: name.trim(), icon_id: iconId ?? '' } },
        { onSuccess: onDone },
      );
      return;
    }
    create.mutate({ name: name.trim(), icon_id: iconId }, { onSuccess: onDone });
  };

  return (
    <Card data-testid="amenity-form">
      <div className="fleet-drawer__head">
        <h2 className="hsc-card__title">{isEdit ? 'Правка удобства' : 'Новое удобство'}</h2>
      </div>
      <form className="hsc-stack" onSubmit={handleSubmit}>
        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="amenity-name">
            Название
          </label>
          <input
            id="amenity-name"
            className="ta-input"
            data-testid="amenity-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Например, Сейф"
            maxLength={64}
            required
          />
        </div>

        <div className="ta-field">
          <span className="hsc-input__label">Иконка</span>
          <IconPicker value={iconId} onChange={setIconId} />
        </div>

        {isError && (
          <p role="alert" className="fleet-card__err" data-testid="amenity-form-error">
            Не удалось сохранить удобство. Проверьте данные и попробуйте снова.
          </p>
        )}

        <div className="menu-form__submit">
          <Button type="submit" disabled={!canSubmit} data-testid="amenity-submit">
            {pending ? 'Сохраняем…' : isEdit ? 'Сохранить' : 'Добавить'}
          </Button>
          <Button type="button" variant="ghost" onClick={onDone} data-testid="amenity-form-close">
            Назад
          </Button>
        </div>
      </form>
    </Card>
  );
}
